import { formatDollarsM, formatPrice } from "../state/format.js";
import type { LinePoint, PaneState } from "../types.js";

interface Props {
  pane: PaneState;
  show0dte: boolean;
  /** The viewed ticker, naming the white price item. */
  symbol: string;
  /** Latest spot for the price item; null hides it. */
  spot: number | null;
  /** When the chart is hovered, readouts rewind to this sampled point. */
  hoverPoint: LinePoint | null;
}

interface Item {
  cls: string;
  name: string;
  value: string;
  dotted: boolean;
}

/**
 * The left pane's value strip: swatch + name + running value per series, laid
 * out ABOVE the plot (in flow) so it can never occlude the chart or its y-axis.
 * Hovering the chart rewinds the values to the hovered moment; leaving snaps
 * back to live.
 */
export function CurrentValues({ pane, show0dte, symbol, spot, hoverPoint }: Props): JSX.Element {
  const call = hoverPoint ? hoverPoint.call : pane.callDeltaNotional;
  const put = hoverPoint ? hoverPoint.put : pane.putDeltaNotional;
  const call0 = hoverPoint ? hoverPoint.call0dte : pane.call0dteNotional;
  const put0 = hoverPoint ? hoverPoint.put0dte : pane.put0dteNotional;
  const px = hoverPoint ? hoverPoint.spot ?? null : spot;

  const items: Item[] = [
    { cls: "tape-val--call", name: "Calls", value: formatDollarsM(call), dotted: false },
    { cls: "tape-val--put", name: "Puts", value: formatDollarsM(put), dotted: false },
  ];
  if (show0dte) {
    items.push(
      { cls: "tape-val--call0dte", name: "Calls 0DTE", value: formatDollarsM(call0), dotted: true },
      { cls: "tape-val--put0dte", name: "Puts 0DTE", value: formatDollarsM(put0), dotted: true },
    );
  }
  if (px !== null) {
    items.push({ cls: "tape-val--spot", name: symbol, value: formatPrice(px), dotted: false });
  }

  return (
    <div className="tape-strip">
      {items.map((it) => (
        <span key={it.name} className={`tape-strip__item ${it.cls}`}>
          <span className={`tape-strip__swatch${it.dotted ? " tape-strip__swatch--dotted" : ""}`} />
          <span className="tape-strip__name">{it.name}</span>
          <span className="tape-strip__val">{it.value}</span>
        </span>
      ))}
    </div>
  );
}
